import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Grid, Stack, Typography, TextField, IconButton, MenuItem, Select, SelectChangeEvent, FormControl, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import { Mutex } from 'async-mutex';
import { ThemeMode } from 'types/config';
const mutex = new Mutex();
let release: any = null;

interface CombinedPaginationProps {
  pageIndex: number;
  pageCount: number;
  rows: any[];
  pageSize: number;
  setPageSize: (size: number) => void;
  gotoPage: (page: number) => void;
  dataChanges?: number;
  showRowsPerPage?: boolean;
  onBack?: () => void;
  typePane?: any;
  seteditFieldID?: React.Dispatch<React.SetStateAction<Number | null>>;
}

const CombinedPagination = ({
  pageIndex,
  pageCount,
  rows,
  pageSize,
  setPageSize,
  gotoPage,
  dataChanges,
  showRowsPerPage = true,
  onBack,
  typePane,
  seteditFieldID
}: CombinedPaginationProps) => {
  const theme = useTheme();
  const isformDirty = useSelector((state: RootState) => state.formslice.isDirty);
  const [pageInput, setPageInput] = useState<string>(String(pageIndex + 1));
  const pageSizes = [5, 10, 15, 20, 50, 100]; // Customize based on your requirements
  const [pageBlock, setPageBlock] = useState<boolean>(false);

  useEffect(() => {
    setPageInput(String(pageIndex + 1)); // Update pageInput when pageIndex changes
  }, [pageIndex]);
  useEffect(() => {
    if (Number(rows.length) === 0 && Number(pageIndex) > 0) {
      gotoPage(pageIndex - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
  useEffect(() => {
    if (dataChanges) {
      gotoPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataChanges]);

  const handlePageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPageInput(event.target.value);
  };

  const handlePageSubmit = useCallback(async () => {
    try {
      const page = parseInt(pageInput);
      if (page > 0 && page <= pageCount && pageIndex + 1 !== Number(pageInput)) {
        if (isformDirty) {
          release = await mutex.acquire();
          setPageBlock(true);
          await mutex.runExclusive(() => undefined);
        }
        gotoPage(page - 1);
      } else {
        throw new Error('Invalid page number');
      }
    } catch {
      setPageInput(String(pageIndex + 1)); // Reset to current page if invalid
    } finally {
      setPageBlock(false);
      release?.();
    }
  }, [gotoPage, isformDirty, pageCount, pageIndex, pageInput]);

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    const newSize = parseInt(event.target.value as string); // Casting to string first, then to number
    if (!isNaN(newSize)) {
      setPageSize(newSize);
    }
  };

  const inputWidth = Math.max(20, pageInput.length * 6);

  const handleLeftRightNavigation = useCallback(
    async (type: 'LEFT' | 'RIGHT') => {
      try {
        if (isformDirty) {
          release = await mutex.acquire();
          setPageBlock(true);
          await mutex.runExclusive(() => undefined);
        }

        if (type === 'LEFT') {
          gotoPage(Math.max(0, pageIndex - 1));
        } else {
          gotoPage(Math.min(pageCount - 1, pageIndex + 1));
        }
      } catch (e) {
        console.log(e);
      } finally {
        setPageBlock(false);
        release?.();
        if (typePane === 'configurationLeftPane') {
          seteditFieldID?.(null);
        }
      }
    },
    [gotoPage, isformDirty, pageCount, pageIndex]
  );

  const onFirstLastNavigation = useCallback(
    async (type: 'FIRST' | 'LAST') => {
      try {
        if (isformDirty) {
          release = await mutex.acquire();
          setPageBlock(true);
          await mutex.runExclusive(() => undefined);
        }

        if (type === 'FIRST') {
          gotoPage(0);
        } else {
          gotoPage(pageCount - 1);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setPageBlock(false);
        release?.();
        if (typePane === 'configurationLeftPane') {
          seteditFieldID?.(null);
        }
      }
    },
    [gotoPage, isformDirty, pageCount]
  );

  return (
    <Grid container className="combinedPagination" alignItems="center">
      {showRowsPerPage && (
        <>
          <Grid item xs={4} md={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="secondary">
                  <FormattedMessage id="elements.tables.rowsPerPage" />
                </Typography>
                <FormControl sx={{ m: 1 }}>
                  <Select
                    id="demo-controlled-open-select"
                    value={pageSize.toString()} // Ensuring the value is a string
                    onChange={handlePageSizeChange}
                    displayEmpty
                    size="small"
                    inputProps={{ 'aria-label': 'Without label' }}
                    sx={{ '& .MuiSelect-select': { py: 0.75, px: 1.25 } }}
                  >
                    {pageSizes.map((size) => (
                      <MenuItem key={size} value={size.toString()}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Grid>
        </>
      )}

      {/* Navigation Controls */}
      <Grid item xs={showRowsPerPage ? 8 : 12} md={showRowsPerPage ? 8 : 12}>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
          {/* First Page Button */}
          <IconButton onClick={() => onFirstLastNavigation('FIRST')} disabled={pageIndex === 0} sx={{ color: theme.palette.text.primary }}>
            <FirstPage color="inherit" />
          </IconButton>
          {/* Previous Page Button */}
          <IconButton
            onClick={() => handleLeftRightNavigation('LEFT')}
            disabled={pageIndex === 0}
            sx={{ color: theme.palette.text.primary }}
          >
            <ChevronLeft color="inherit" />
          </IconButton>
          {/* Current Page Input and Total Page Count */}
          <TextField
            type="number"
            className="pageNumber"
            value={pageInput}
            onChange={handlePageChange}
            onBlur={handlePageSubmit}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handlePageSubmit();
            }}
            sx={{
              '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0
              },
              '& input[type=number]': {
                '-moz-appearance': 'textfield', // For Firefox
                padding: '10px', // Adding padding
                border: 'none', // Removing border
                boxSizing: 'unset',
                height: 10,
                // color: theme.palette.secondary.colorNeutrals?.neutrals70,
                backgroundColor:
                  theme.palette.mode === ThemeMode.DARK
                    ? theme.palette.background.default
                    : theme.palette.secondary.colorNeutrals?.neutrals20
              }
            }}
            size="small"
            inputProps={{
              min: 1,
              max: pageCount,
              style: { textAlign: 'center', width: `${inputWidth}px` }
            }}
          />
          <Typography variant="body2">
            /&nbsp;&nbsp;<span>{pageCount}</span>
          </Typography>
          {/* Next Page Button */}
          <IconButton
            onClick={() => handleLeftRightNavigation('RIGHT')}
            disabled={pageIndex >= pageCount - 1}
            sx={{ color: theme.palette.text.primary }}
          >
            <ChevronRight color="inherit" />
          </IconButton>
          {/* Last Page Button */}
          <IconButton
            onClick={() => onFirstLastNavigation('LAST')}
            disabled={pageIndex === pageCount - 1}
            sx={{ color: theme.palette.text.primary }}
          >
            <LastPage color="inherit" />
          </IconButton>
        </Stack>
      </Grid>
      <ConfirmationDialog
        open={pageBlock}
        onClose={() => mutex.cancel()}
        onConfirm={() => release()}
        isLoading={false}
        confirmationKey="navigateAway"
        alertMessage="You have unsaved changes that will be lost, if you decide to leave this page."
        message="Are you sure you want to leave this page?"
        confirmButtonLabel="Leave this page"
        cancelButtonLabel="Stay on this page"
        type="unsavedChanges"
        customMaxWidth="510px"
      />
    </Grid>
  );
};

export default CombinedPagination;
